import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { academyApi } from "@/lib/academy";
import { BELT_COLORS, BELT_INK } from "@/lib/academy";

/** Start → questions → submit → feedback. scope: {kind:'lesson'|'belt', id} */
export default function AcademyExam({ scope, onPassed, onClose }) {
  const [state, setState] = useState({ phase: "idle", questions: [], attemptId: null, answers: {}, idx: 0, result: null, feedback: null, error: "" });

  async function start() {
    setState({ ...state, phase: "loading", error: "" });
    try {
      const res = scope.kind === "lesson" ? await academyApi.startSection(scope.id)
        : scope.kind === "final" ? await academyApi.startFinal()
        : await academyApi.startBeltExam(scope.id);
      setState({ ...state, phase: "open", questions: res.questions, attemptId: res.attemptId, idx: 0, answers: {}, result: null, feedback: null, certToken: null });
    } catch (e) {
      setState({ ...state, phase: "idle", error: e.response?.data?.detail || "Could not start the exam." });
    }
  }

  async function submit() {
    setState({ ...state, phase: "grading" });
    try {
      const res = await academyApi.submitExam(state.attemptId, state.answers);
      setState({ ...state, phase: "done", result: res.result, feedback: res.feedback, certToken: res.comprehensiveCertToken || null });
      if (res.result?.knowledge_passed) onPassed?.(res);
    } catch (e) {
      setState({ ...state, phase: "open", error: e.response?.data?.detail || "Submission failed." });
    }
  }

  const q = state.questions[state.idx];
  const answered = Object.keys(state.answers).length;

  if (state.phase === "done") {
    return (
      <div className="academy-exam" data-testid="academy-exam-result">
        <h3>{state.result?.knowledge_passed ? "PASSED" : "Not passed yet"}</h3>
        <p>Knowledge score: <strong>{state.result?.score}%</strong> · Threshold {state.result?.threshold}% · All safety-critical items correct: {state.result?.critical_passed ? "yes" : "no"}</p>
        {state.certToken && (
          <div style={{ margin: "12px 0", padding: 14, border: "2px solid var(--teal)", borderRadius: 12 }}>
            <strong>🏆 Comprehensive exam passed — Certified Solar &amp; Powerwall Professional issued!</strong>
            <p className="micro">View and download it on your <Link to={`/academy/certificates/${state.certToken}`}>certificate page</Link>.</p>
          </div>
        )}
        <div className="academy-feedback">
          {state.feedback?.map((f) => (
            <p key={f.question_id} style={{ color: f.correct ? "var(--teal)" : "#a33" }}>
              {f.correct ? "✓" : "✗"} {f.question_id}: {f.rationale?.slice(0, 180)}
            </p>
          ))}
        </div>
        <button className="btn small" onClick={onClose}>Close</button>
      </div>
    );
  }

  return (
    <div className="academy-exam" data-testid="academy-exam">
      {state.phase === "idle" && (
        <>
          <h3>{scope.kind === "belt" ? "Belt exam" : scope.kind === "final" ? "The official comprehensive exam" : "Section-end graded assessment"}</h3>
          <p className="micro">Graded server-side. {scope.kind === "belt" ? "Covers this belt plus earlier material and vocabulary." : scope.kind === "final" ? "Every vocabulary word plus knowledge from all eight belts." : "Three knowledge questions plus this lesson's vocabulary."} Practice is separate from exams.</p>
          {state.error && <p className="micro" style={{ color: "#a33" }}>{state.error}</p>}
          <button className="btn small lime" data-testid="academy-exam-start" onClick={start}>Start graded assessment</button>
        </>
      )}
      {state.phase === "loading" && <p>Preparing your exam…</p>}
      {(state.phase === "open" || state.phase === "grading") && q && (
        <>
          <p className="micro">Question {state.idx + 1} of {state.questions.length} · {answered} answered</p>
          <p><strong>{q.stem}</strong></p>
          {q.choices.map((c) => (
            <label key={c.id} className="academy-choice" style={{ borderColor: state.answers[q.id]?.[0] === c.id ? "var(--teal)" : "var(--line)" }}>
              <input type="radio" name={q.id} checked={state.answers[q.id]?.[0] === c.id} onChange={() => setState({ ...state, answers: { ...state.answers, [q.id]: [c.id] } })} />
              {c.text}
            </label>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            {state.idx > 0 && <button className="btn small" onClick={() => setState({ ...state, idx: state.idx - 1 })}>← Back</button>}
            {state.idx < state.questions.length - 1
              ? <button className="btn small lime" disabled={!state.answers[q.id]} onClick={() => setState({ ...state, idx: state.idx + 1 })}>Next →</button>
              : <button className="btn small lime" data-testid="academy-exam-submit" disabled={answered < state.questions.length || state.phase === "grading"} onClick={submit}>Submit for grading</button>}
          </div>
          {state.error && <p className="micro" style={{ color: "#a33" }}>{state.error}</p>}
        </>
      )}
    </div>
  );
}
